import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  KeyRound,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { createRecentReauthentication } from "@/api/auth";
import { ApiError, apiErrorMessage } from "@/api/client";
import {
  cancelKintoneEnvironmentSwitchOperation,
  confirmKintoneEnvironmentSwitchOperation,
  createKintoneEnvironmentSwitchOperation,
  fetchKintoneEnvironmentSwitchOperation,
  verifyKintoneEnvironmentOwnership,
  type EnvironmentSwitchOperation,
  type OwnershipScanContinuationResponse,
  type OwnershipVerificationResponse,
} from "@/api/kintone-environment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { useKintoneEnvironment } from "./hooks/useKintoneEnvironment";

export function KintoneEnvironmentSwitchPage() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const { environment, isLoading, error: environmentError, refresh } =
    useKintoneEnvironment();
  const [targetSubdomain, setTargetSubdomain] = useState("");
  const [kintoneAppUrl, setKintoneAppUrl] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [ownershipScan, setOwnershipScan] =
    useState<OwnershipScanContinuationResponse | null>(null);
  const [guestSpaceId, setGuestSpaceId] = useState("");
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [operation, setOperation] = useState<EnvironmentSwitchOperation | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [postCutoverSyncWarning, setPostCutoverSyncWarning] = useState<string | null>(null);
  const completed = operation?.cutoverCommittedAt != null;

  useEffect(() => {
    const active = environment?.currentOperation;
    if (!active) return;
    void fetchKintoneEnvironmentSwitchOperation(active.operationId)
      .then(setOperation)
      .catch((cause) => setMessage(apiErrorMessage(cause, "進行中の環境移行を取得できませんでした。")));
  }, [environment?.currentOperation]);

  if (isLoading && !completed) {
    return <Page><StatusLine><Loader2 className="h-4 w-4 animate-spin" />現在環境を確認しています</StatusLine></Page>;
  }
  if (!completed && (!environment || environmentError ||
      environment.kintoneConnectionState !== "registered" ||
      !environment.kintoneSubdomain)) {
    return <Page><ErrorSummary message={environmentError ?? "登録済みのkintone環境がありません。"} /><Button variant="outline" onClick={() => void refresh()}><RefreshCw className="mr-2 h-4 w-4" />再読み込み</Button></Page>;
  }

  async function begin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalized = targetSubdomain.trim().toLowerCase().replace(/\.cybozu\.com$/, "");
    if (!/^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/.test(normalized)) {
      setMessage("3〜63文字のkintoneサブドメインを入力してください。");
      return;
    }
    if (normalized === environment!.kintoneSubdomain) {
      setMessage("現在と異なるサブドメインを入力してください。");
      return;
    }
    const usesSavedAppCredentials = environment!.appBindingCount > 0;
    if (!usesSavedAppCredentials && (!kintoneAppUrl.trim() || !apiToken.trim())) {
      setMessage("移行先の支配確認に使うアプリURLとAPIトークンを入力してください。");
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const verification = await continueUntilInputOrChallenge(
        usesSavedAppCredentials
          ? {
              verificationMethod: "saved_app_credentials",
              targetSubdomain: normalized,
              connectionAccess: { kind: "standard" },
            }
          : {
              verificationMethod: "new_api_token",
              targetSubdomain: normalized,
              kintoneAppUrl: kintoneAppUrl.trim(),
              apiToken,
              connectionAccess: { kind: "standard" },
            },
      );
      if ("state" in verification) {
        setOwnershipScan(verification);
        return;
      }
      await startDiagnosis(verification);
      setApiToken("");
    } catch (cause) {
      setMessage(apiErrorMessage(cause, "移行先の診断を開始できませんでした。"));
    } finally {
      setApiToken("");
      setBusy(false);
    }
  }

  async function continueUntilInputOrChallenge(
    request: Parameters<typeof verifyKintoneEnvironmentOwnership>[0],
  ) {
    return verifyKintoneEnvironmentOwnership(request);
  }

  async function startDiagnosis(challenge: OwnershipVerificationResponse) {
    if (challenge.connectionAccessKind !== "standard") {
      throw new Error("移行先の接続方法が確認要求と一致しません。");
    }
    const result = await createKintoneEnvironmentSwitchOperation({
      challengeId: challenge.challengeId,
      targetSubdomain: challenge.targetSubdomain,
      expectedEnvironmentGeneration: environment!.environmentGeneration,
      connectionAccess: { kind: "standard" },
    }, `environment-switch:${crypto.randomUUID()}`);
    setOwnershipScan(null);
    setOperation(result);
  }

  async function continueWithScopeDecision(
    decision: { kind: "regular_space" } | { kind: "guest_space"; guestSpaceId: string },
  ) {
    if (ownershipScan?.state !== "target_scope_input_required") return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await continueUntilInputOrChallenge({
        verificationMethod: "saved_app_credentials",
        targetSubdomain: targetSubdomain.trim().toLowerCase().replace(/\.cybozu\.com$/, ""),
        connectionAccess: { kind: "standard" },
        scanId: ownershipScan.scanId,
        targetScopeDecision: decision,
      });
      if ("state" in result) setOwnershipScan(result);
      else await startDiagnosis(result);
    } catch (cause) {
      setMessage(apiErrorMessage(cause, "移行先の確認を継続できませんでした。"));
    } finally {
      setBusy(false);
    }
  }

  async function resumeOwnershipScan() {
    if (!ownershipScan || ownershipScan.state === "target_scope_input_required") return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await continueUntilInputOrChallenge({
        verificationMethod: "saved_app_credentials",
        targetSubdomain: targetSubdomain.trim().toLowerCase().replace(/\.cybozu\.com$/, ""),
        connectionAccess: { kind: "standard" },
        scanId: ownershipScan.scanId,
        ...(ownershipScan.state === "inactive_expired"
          ? { continuationCursor: ownershipScan.continuationCursor }
          : {}),
      });
      if ("state" in result) setOwnershipScan(result);
      else await startDiagnosis(result);
    } catch (cause) {
      setMessage(apiErrorMessage(cause, "所有確認スキャンを再開できませんでした。"));
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!operation || !password) {
      setMessage("現在のログインパスワードを入力してください。");
      return;
    }
    if (operation.totalAppCount > 0 && !accepted) {
      setMessage("登録済みの全アプリが移行対象になることを確認してください。");
      return;
    }
    setBusy(true);
    setMessage(null);
    setPostCutoverSyncWarning(null);
    try {
      await createRecentReauthentication(password);
      const result = await confirmKintoneEnvironmentSwitchOperation(
        operation.operationId,
        {
          expectedOperationRevision: operation.revision,
          expectedEnvironmentGeneration: operation.sourceEnvironmentGeneration,
          appBindingSetDigest: operation.appBindingSetDigest,
          diagnosisWarningDigest: operation.diagnosisWarningDigest,
          allApplicationsWillSwitchAccepted: true,
          connectionAccess: { kind: "standard" },
        },
      );
      await settleConfirmationResult(result);
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status >= 500) {
        try {
          const readback = await fetchKintoneEnvironmentSwitchOperation(operation.operationId);
          await settleConfirmationResult(readback);
          if (readback.cutoverCommittedAt !== null) return;
        } catch (readbackCause) {
          setMessage(apiErrorMessage(
            readbackCause,
            "環境移行の確認結果を取得できませんでした。ページを再読み込みしてください。",
          ));
          return;
        }
      }
      setMessage(apiErrorMessage(cause, "kintone環境を移行できませんでした。"));
    } finally {
      setPassword("");
      setBusy(false);
    }
  }

  async function settleConfirmationResult(result: EnvironmentSwitchOperation) {
    setOperation(result);
    setPassword("");
    if (result.cutoverCommittedAt === null) return;
    const [environmentRefresh, authRefresh] = await Promise.allSettled([
      refresh(),
      refreshMe(),
    ]);
    const environmentSynchronized =
      environmentRefresh.status === "fulfilled" && environmentRefresh.value;
    if (!environmentSynchronized || authRefresh.status === "rejected") {
      setPostCutoverSyncWarning(
        "kintone環境の移行は完了しましたが、表示を更新できませんでした。ページを再読み込みしてください。",
      );
    }
  }

  async function cancel() {
    if (!operation) return;
    setBusy(true);
    setMessage(null);
    try {
      await cancelKintoneEnvironmentSwitchOperation(
        operation.operationId,
        operation.revision,
        `environment-switch-cancel:${crypto.randomUUID()}`,
      );
      setOperation(null);
      setAccepted(false);
      await refresh();
    } catch (cause) {
      setMessage(apiErrorMessage(cause, "環境移行を取り消せませんでした。"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page>
      <Button variant="ghost" className="-ml-3 mb-4" disabled={busy || postCutoverSyncWarning !== null} onClick={() => navigate("/console/app-bindings")}><ArrowLeft className="mr-2 h-4 w-4" />kintoneアプリへ戻る</Button>
      <header className="max-w-3xl border-b pb-5">
        <h1 className="text-2xl font-bold tracking-tight">kintone環境を移行</h1>
        <p className="mt-2 text-sm text-muted-foreground">現在: {completed ? operation.targetSubdomain : environment!.kintoneSubdomain}.cybozu.com</p>
      </header>

      {message ? <div className="mt-5"><ErrorSummary message={message} /></div> : null}
      {postCutoverSyncWarning ? <div className="mt-5"><WarningSummary message={postCutoverSyncWarning} /></div> : null}

      {!operation ? (
        <form className="mt-6 max-w-3xl space-y-5" onSubmit={(event) => void begin(event)}>
          <div><h2 className="text-lg font-semibold">移行先を確認</h2><p className="mt-1 text-sm text-muted-foreground">{environment!.appBindingCount > 0 ? "保存済みのアプリ設定を順に使い、移行先のサブドメインを確認します。アプリIDとAPIトークンの再入力は不要です。" : "登録済みアプリがないため、移行先で読み取り可能なアプリを使ってサブドメインを確認します。この入力値は保存しません。"}</p></div>
          <div className="space-y-2"><Label htmlFor="target-subdomain">新しいサブドメイン</Label><div className="flex max-w-lg items-center gap-2"><Input id="target-subdomain" value={targetSubdomain} onChange={(event) => setTargetSubdomain(event.target.value)} autoFocus disabled={busy || ownershipScan !== null} /><span className="shrink-0 text-sm text-muted-foreground">.cybozu.com</span></div></div>
          {environment!.appBindingCount === 0 ? (
            <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
              <div className="space-y-2"><Label htmlFor="ownership-app-url">支配確認用アプリURL</Label><Input id="ownership-app-url" type="url" value={kintoneAppUrl} onChange={(event) => setKintoneAppUrl(event.target.value)} placeholder={`https://${targetSubdomain || "subdomain"}.cybozu.com/k/123/`} disabled={busy} /></div>
              <div className="space-y-2"><Label htmlFor="ownership-token">APIトークン</Label><div className="relative"><KeyRound className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input id="ownership-token" className="pl-9" type="password" autoComplete="off" value={apiToken} onChange={(event) => setApiToken(event.target.value)} disabled={busy} /></div></div>
            </div>
          ) : null}
          {ownershipScan?.state === "target_scope_input_required" ? (
            <section className="max-w-2xl border-l-4 border-amber-500 pl-4" aria-live="polite">
              <p className="font-medium">{ownershipScan.appDisplayName} の移行先スペースを確認してください</p>
              <p className="mt-1 text-sm text-muted-foreground">アプリIDとAPIトークンの再入力は不要です。</p>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <Button type="button" variant="outline" disabled={busy} onClick={() => void continueWithScopeDecision({ kind: "regular_space" })}>通常スペースまたはスペース外</Button>
                <div className="space-y-1"><Label htmlFor="target-guest-space-id">ゲストスペースID</Label><Input id="target-guest-space-id" className="w-40" inputMode="numeric" value={guestSpaceId} onChange={(event) => setGuestSpaceId(event.target.value.replace(/\D/g, ""))} disabled={busy} /></div>
                <Button type="button" disabled={busy || !/^[1-9]\d*$/.test(guestSpaceId)} onClick={() => void continueWithScopeDecision({ kind: "guest_space", guestSpaceId })}>ゲストスペースで確認</Button>
              </div>
            </section>
          ) : null}
          {ownershipScan?.state === "waiting_retry" || ownershipScan?.state === "inactive_expired" ? (
            <section aria-live="polite" className="max-w-2xl border-l-4 border-amber-500 pl-4">
              <p className="text-sm text-muted-foreground">
                {ownershipScan.state === "waiting_retry"
                  ? "一時的に確認を完了できませんでした。同じアプリから再開できます。"
                  : "無操作時間を超えました。署名済みの継続情報から再開できます。"}
              </p>
              <Button className="mt-3" type="button" variant="outline" disabled={busy} onClick={() => void resumeOwnershipScan()}>スキャンを再開</Button>
            </section>
          ) : null}
          <Button type="submit" disabled={busy || ownershipScan !== null}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}確認して診断</Button>
        </form>
      ) : completed ? (
        <section className="mt-6 max-w-3xl border-l-4 border-emerald-500 pl-5" aria-live="polite">
          <div className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /><h2 className="text-lg font-semibold">kintone環境を移行しました</h2></div>
          <p className="mt-2 text-sm text-muted-foreground">{operation.targetSubdomain}.cybozu.com を現在環境として使用します。</p>
          {operation.reconciliations.length > 0 ? (
            <p className="mt-2 text-sm text-amber-700">登録済みアプリは旧環境の接続資格情報を失効済みです。kintoneアプリ画面から移行先へプラグイン設定を再反映してください。</p>
          ) : null}
          {operation.state === "reconciling" ? (
            <p className="mt-2 text-sm text-muted-foreground">ストレージ接続の許可元を確認しています。現在環境は移行先に確定済みです。</p>
          ) : null}
          {operation.state === "manual_repair_required" ? (
            <p className="mt-2 text-sm text-red-700">ストレージ接続の許可元を自動確認できませんでした。現在環境は移行先のままです。サポートへお問い合わせください。</p>
          ) : null}
          <Button className="mt-5" disabled={busy || postCutoverSyncWarning !== null} onClick={() => navigate("/console/app-bindings")}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}kintoneアプリを確認</Button>
        </section>
      ) : (
        <section className="mt-6 max-w-4xl space-y-6">
          <div><h2 className="text-lg font-semibold">診断結果</h2><p className="mt-1 text-sm text-muted-foreground">{operation.sourceSubdomain}.cybozu.com から {operation.targetSubdomain}.cybozu.com へ移行します。</p></div>
          {operation.totalAppCount === 0 ? (
            <div className="border-l-4 border-emerald-500 pl-4"><p className="font-medium">登録済みアプリはありません</p><p className="mt-1 text-sm text-muted-foreground">アプリへの影響確認は不要です。本人確認後すぐに移行できます。</p></div>
          ) : (
            <>
              <div className="border-l-4 border-amber-500 pl-4"><div className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /><p className="font-semibold">登録済みの全{operation.totalAppCount}件が新しい環境へ移行します</p></div><p className="mt-1 text-sm text-muted-foreground">不整合は移行を阻害しません。移行後にアプリ単位で補正します。</p></div>
              <div className="overflow-hidden rounded-md border">
                <table className="w-full text-sm"><thead className="bg-muted/50 text-left"><tr><th className="px-4 py-3 font-medium">アプリ</th><th className="px-4 py-3 font-medium">診断</th><th className="px-4 py-3 font-medium">確認内容</th></tr></thead><tbody>{operation.diagnosis.map((item) => <tr key={item.appBindingId} className="border-t"><td className="px-4 py-3"><div className="font-medium">{item.expectedKintoneAppName}</div><div className="text-xs text-muted-foreground">ID: {item.expectedKintoneAppId}</div></td><td className="px-4 py-3">{item.status === "healthy" ? "一致" : item.status === "warning" ? "要確認" : "未確認"}</td><td className="px-4 py-3 text-muted-foreground">{item.reasons.length ? item.reasons.map(reasonLabel).join("、") : "接続を確認できました"}</td></tr>)}</tbody></table>
              </div>
              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-4"><input className="mt-1 h-4 w-4" type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} /><span><span className="block font-medium">登録済みの全アプリの接続先が移行することを確認しました</span><span className="mt-1 block text-sm text-muted-foreground">警告があるアプリも移行後の補正対象として進めます。</span></span></label>
            </>
          )}
          <div className="border-t pt-5"><h3 className="font-semibold">本人確認</h3><p className="mt-1 text-sm text-muted-foreground">環境移行を確定するため、現在のログインパスワードを入力してください。</p><div className="mt-3 max-w-sm space-y-2"><Label htmlFor="reauth-password">ログインパスワード</Label><Input id="reauth-password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} disabled={busy} /></div></div>
          <div className="flex flex-wrap gap-3"><Button onClick={() => void confirm()} disabled={busy || !password || (operation.totalAppCount > 0 && !accepted)}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}環境を移行</Button><Button variant="outline" onClick={() => void cancel()} disabled={busy}>取り消す</Button></div>
        </section>
      )}
    </Page>
  );
}

function Page({ children }: { children: React.ReactNode }) {
  return <div className="p-6">{children}</div>;
}

function StatusLine({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-2 text-sm text-muted-foreground">{children}</div>;
}

function ErrorSummary({ message }: { message: string }) {
  return <div role="alert" className="max-w-3xl rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">{message}</div>;
}

function WarningSummary({ message }: { message: string }) {
  return <div role="alert" className="max-w-3xl rounded-md border border-amber-500/40 bg-amber-50 px-4 py-3 text-sm text-amber-800">{message}</div>;
}

function reasonLabel(reason: string) {
  const labels: Record<string, string> = {
    app_not_found: "アプリIDが見つかりません",
    app_unreachable: "アプリを確認できません",
    app_name_mismatch: "アプリ名が異なります",
    api_token_missing: "APIトークンがありません",
    api_token_unauthorized: "APIトークンを認証できません",
    api_token_forbidden: "APIトークンの権限が不足しています",
    api_token_rate_limited: "kintoneの制限により未確認です",
    api_token_timeout: "kintoneへの接続がタイムアウトしました",
    api_token_server_failure: "kintoneへの接続を確認できません",
    field_mismatch: "フィールド設定が異なります",
    plugin_configuration_mismatch: "プラグイン設定が異なります",
  };
  return labels[reason] ?? "確認が必要です";
}
